import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  concatMap,
  exhaustMap,
  filter,
  from,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import { Option, Result } from '@sapphire/result';

import { Nullable } from '@ps/utils';
import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { ProjectService, SurveyService } from '@ps/survey-data-access';
import {
  ProjectUpdatePayloadInput,
  resolveTrackerWaveId,
  ScheduleLaunch,
  Survey,
  SURVEY_MESSAGES,
  TRACKER_MAXIMUM_WAVE_COUNT,
  TRACKER_MESSAGES,
  TRACKER_MINIMUM_WAVE_COUNT,
  TrackerUpdateChildInput,
} from '@ps/survey-utils';

import { isTrackerWaveSequenceValid } from './utils/tracker-wave-sequence-validator';

import {
  selectFeasibilityCpi,
  selectInsightsChildren,
  selectPricing,
} from '../insights';

import {
  SurveyCreationTrackerActions,
  SurveyCreationTrackerApiActions,
} from './survey-creation-audience.actions';
import {
  selectAudienceChildren,
  selectSuppliersMetaData,
} from './survey-creation-audience.selectors';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectTrackerConfig,
} from '../core/survey-creation-core.selectors';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from '../core/survey-creation-core.actions';
import {
  buildPreDraftChildDrafts,
  buildPreDraftChildDraftsContext,
} from './utils/pre-draft-save.helper';
import { ProjectUpdateInputBuilder } from '../../services/save/project-update-input.builder';
import {
  appendTrackerWave,
  ProjectSaveMessages,
  ProjectUpdateInput,
  removeTrackerWave,
  updateTrackerWaveLaunch,
} from './utils/project-save.helper';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyChildRegistryState } from '../survey-creation-state.model';
import {
  childFilterOrProjectId,
  EVERY_STATUS_OPTIONS,
} from '../core/utils/child-survey-filter.helper';
import { participantRecordKeys } from '../survey-creation-child-registry.helper';

@Injectable()
export class SurveyCreationTrackerSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _projectService = inject(ProjectService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);
  private readonly _projectUpdateInput = inject(ProjectUpdateInputBuilder);

  public readonly createTrackerProject$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.createTrackerProject),
      concatLatestFrom(() => [
        this._store.select(selectAudienceChildren),
        this._store.select(selectCoreChildren),
        this._store.select(selectPricing),
        this._store.select(selectFeasibilityCpi),
        this._store.select(selectInsightsChildren),
        this._store.select(selectTrackerConfig),
      ]),
      map(
        ([
          ,
          children,
          coreChildren,
          pricingState,
          feasibilityCpi,
          insightsChildren,
          trackerConfig,
        ]) => {
          const context = buildPreDraftChildDraftsContext({
            children,
            coreChildren,
            pricingState,
            feasibilityCpi,
            insightsChildren,
            user: this._authSelectors.user(),
            companyId: this._authSelectors.activeCompanyId(),
            noSignedInUserMessage: TRACKER_MESSAGES.NO_SIGNED_IN_USER,
            tooFewSelectedMessage: TRACKER_MESSAGES.SELECT_AT_LEAST_TWO_WAVES,
            minimumSelected: TRACKER_MINIMUM_WAVE_COUNT,
            invalidChildMessage: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
          });

          return [
            context.andThen(buildPreDraftChildDrafts),
            Option.from(trackerConfig),
          ] as const;
        },
      ),
      tap(([drafts]) =>
        drafts.inspectErr((message) => this._toastService.error(message)),
      ),
      filter(([drafts]) => drafts.isOk()),
      exhaustMap(([drafts, trackerConfig]) => {
        const unwrappedDrafts = drafts.unwrap();

        return trackerConfig.match({
          some: (config) =>
            this._projectService
              .createTrackerProject({
                children: unwrappedDrafts.map((draft) => draft.input),
                trackerConfig: {
                  ...config,
                  numberOfWaves: unwrappedDrafts.length,
                },
              })
              .pipe(
                map((survey) => {
                  this._toastService.success(SURVEY_MESSAGES.SAVED, {
                    keepOpen: false,
                  });
                  return SurveyCreationTrackerApiActions.createTrackerProjectSuccess(
                    { survey },
                  );
                }),
                this.psCatchError(
                  (error) =>
                    of(
                      SurveyCreationTrackerApiActions.createTrackerProjectFailure(
                        { error: error.message },
                      ),
                    ),
                  { notify: true },
                ),
              ),
          none: () => {
            this._toastService.error(TRACKER_MESSAGES.WAVE_NOT_SAVEABLE);

            return of(
              SurveyCreationTrackerApiActions.createTrackerProjectFailure({
                error: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
              }),
            );
          },
        });
      }),
    ),
  );

  public readonly addWave$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.addWave),
      concatLatestFrom(() => [
        this._store.select(selectCoreChildren),
        this._store.select(selectActiveChildKey),
        this._store.select(selectSuppliersMetaData),
      ]),
      exhaustMap(([, coreChildren, activeChildKey, suppliersMetaData]) =>
        this._addWave(
          coreChildren,
          activeChildKey,
          !!suppliersMetaData.priceOverride,
        ),
      ),
    ),
  );

  public readonly deleteWave$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.deleteWave),
      concatLatestFrom(() => [
        this._store.select(selectCoreChildren),
        this._store.select(selectActiveChildKey),
      ]),
      exhaustMap(([{ surveyId }, coreChildren, activeChildKey]) =>
        this._deleteWave(surveyId, coreChildren, activeChildKey),
      ),
    ),
  );

  public readonly updateLaunchDate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.updateLaunchDate),
      exhaustMap(({ surveyId, scheduleLaunch }) =>
        this._updateLaunchDate(surveyId, scheduleLaunch),
      ),
    ),
  );

  private _addWave(
    coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
    activeChildKey: Nullable<SurveyChildKey>,
    hasGlobalPriceOverride: boolean,
  ): Observable<Action> {
    const knownKeys = participantRecordKeys(coreChildren);

    if (knownKeys.size >= TRACKER_MAXIMUM_WAVE_COUNT) {
      return this._rejectWith(
        TRACKER_MESSAGES.MAXIMUM_WAVES_EXCEEDED,
        (error) => SurveyCreationTrackerApiActions.addWaveFailure({ error }),
      );
    }

    return this._runTrackerProjectUpdate(
      (built) =>
        appendTrackerWave(built, activeChildKey, hasGlobalPriceOverride),
      (surveys) =>
        Option.from(
          surveys.find((survey) => !knownKeys.has(String(survey.id))),
        ).match({
          some: (survey) => {
            this._toastService.success(TRACKER_MESSAGES.WAVE_ADDED, {
              keepOpen: false,
            });
            return [SurveyCreationTrackerApiActions.addWaveSuccess({ survey })];
          },
          none: () => {
            this._toastService.error(TRACKER_MESSAGES.WAVE_NOT_SAVEABLE);
            return [
              SurveyCreationTrackerApiActions.addWaveFailure({
                error: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
              }),
            ];
          },
        }),
      (error) => SurveyCreationTrackerApiActions.addWaveFailure({ error }),
    );
  }

  private _deleteWave(
    surveyId: number,
    coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
    activeChildKey: Nullable<SurveyChildKey>,
  ): Observable<Action> {
    if (
      participantRecordKeys(coreChildren).size <= TRACKER_MINIMUM_WAVE_COUNT
    ) {
      return this._rejectWith(
        TRACKER_MESSAGES.WAVE_CANNOT_DELETE_LAST_TWO,
        (error) => SurveyCreationTrackerApiActions.deleteWaveFailure({ error }),
      );
    }

    return this._runTrackerProjectUpdate(
      (built) => removeTrackerWave(built, surveyId),
      (surveys) => {
        this._toastService.success(TRACKER_MESSAGES.WAVE_DELETED, {
          keepOpen: false,
        });
        return [
          SurveyCreationTrackerApiActions.deleteWaveSuccess({
            surveyId,
            surveys,
          }),
          ...this._reselectAfterDelete(surveys, surveyId, activeChildKey),
        ];
      },
      (error) => SurveyCreationTrackerApiActions.deleteWaveFailure({ error }),
    );
  }

  private _updateLaunchDate(
    surveyId: number,
    scheduleLaunch: ScheduleLaunch,
  ): Observable<Action> {
    return this._runTrackerProjectUpdate(
      (built) => updateTrackerWaveLaunch(built, surveyId, scheduleLaunch),
      (surveys) => {
        const sequenceValid = isTrackerWaveSequenceValid(
          surveys.map((survey) => ({
            waveId: resolveTrackerWaveId(survey),
            dateTimeInIso: survey.scheduleLaunch?.dateTimeInIso ?? null,
          })),
        );
        this._toastService.success(
          sequenceValid
            ? TRACKER_MESSAGES.WAVE_LAUNCH_DATE_SAVED
            : TRACKER_MESSAGES.WAVE_LAUNCH_DATE_SAVED_WAVES_REORDERED,
          { keepOpen: false },
        );
        return [
          SurveyCreationTrackerApiActions.updateLaunchDateSuccess({
            surveyId,
            surveys,
          }),
        ];
      },
      (error) =>
        SurveyCreationTrackerApiActions.updateLaunchDateFailure({ error }),
    );
  }

  private _runTrackerProjectUpdate(
    mutate: (
      built: ProjectUpdateInput,
    ) => Result<ProjectUpdatePayloadInput<TrackerUpdateChildInput>, string>,
    onReload: (surveys: Survey[]) => Action[],
    onFailure: (error: string) => Action,
  ): Observable<Action> {
    const messages: ProjectSaveMessages = {
      noChildrenSelected: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
      childNotSaveable: TRACKER_MESSAGES.WAVE_NOT_SAVEABLE,
    };

    return this._projectUpdateInput.build(messages).match({
      some: (built) =>
        mutate(built).match({
          ok: (payload) =>
            this._writeTrackerProjectUpdate(
              built.project,
              payload,
              onReload,
              onFailure,
            ),
          err: (error) => this._rejectWith(error, onFailure),
        }),
      none: () =>
        this._rejectWith(TRACKER_MESSAGES.WAVE_NOT_SAVEABLE, onFailure),
    });
  }

  private _writeTrackerProjectUpdate(
    project: Survey,
    payload: ProjectUpdatePayloadInput<TrackerUpdateChildInput>,
    onReload: (surveys: Survey[]) => Action[],
    onFailure: (error: string) => Action,
  ): Observable<Action> {
    return this._projectService.updateTrackerProject(project.id, payload).pipe(
      concatMap(() =>
        this._surveyService
          .getChildSurveys(
            childFilterOrProjectId(project),
            EVERY_STATUS_OPTIONS,
          )
          .pipe(concatMap((surveys) => from(onReload(surveys)))),
      ),
      this.psCatchError((error) => of(onFailure(error.message)), {
        notify: true,
      }),
    );
  }

  private _rejectWith(
    error: string,
    onFailure: (error: string) => Action,
  ): Observable<Action> {
    this._toastService.error(error);
    return of(onFailure(error));
  }

  private _reselectAfterDelete(
    surveys: readonly Survey[],
    deletedSurveyId: number,
    activeChildKey: Nullable<SurveyChildKey>,
  ): Action[] {
    if (String(activeChildKey) !== String(deletedSurveyId)) {
      return [];
    }

    return Option.from(
      [...surveys].sort(
        (left, right) =>
          resolveTrackerWaveId(left) - resolveTrackerWaveId(right),
      )[0],
    ).mapOr([], (survey) => [
      SurveyCreationCoreActions.setActiveChildKey({
        activeChildKey: survey.id,
      }),
      SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess({ survey }),
    ]);
  }
}
